import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CASE_TRANSITIONS } from '@hospasense/contracts';
import { ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useCaseDetail, useCaseMutation } from '../lib/queries-s2.js';
import { formatDateTime, titleCase } from '../lib/format.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { CASE_STATUS_TONES, SlaCell } from './SupportPage.js';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ErrorState,
  LoadingState,
  Modal,
  PageHeader,
  PermissionDeniedState,
  Select,
  StatusPill,
  Textarea,
  useToast,
} from '../components/ui/index.js';

/** Case workspace: SLA, append-only event log, communications, attachments. */
export function CaseDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const query = useCaseDetail(id);
  const mutations = useCaseMutation(id);
  const toast = useToast();
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [toStatus, setToStatus] = useState('');
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  if (!hasPermission('case.read')) return <PermissionDeniedState what="support cases" />;
  if (query.isLoading) return <LoadingState label="Loading case…" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        description="This case could not be loaded."
        onRetry={() => void query.refetch()}
      />
    );
  }
  const { case: c, events, attachments } = query.data;
  const canManage = hasPermission('case.manage');
  const targets = CASE_TRANSITIONS[c.status];

  const upload = (file: File): void => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataBase64 = String(reader.result).split(',')[1] ?? '';
      void mutations.attach
        .mutateAsync({ filename: file.name, contentType: file.type || 'text/plain', dataBase64 })
        .then(() => toast.success('Attachment accepted'))
        .catch((err) => toast.error(err instanceof ApiError ? err.message : 'Attachment rejected'));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={`#${c.caseNumber} ${c.title}`}
        subtitle={`${c.organisationName} · opened by ${c.openedByDisplayName ?? '—'} · ${formatDateTime(c.openedAt)}`}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: 'Control', to: '/' },
              { label: 'Support', to: '/support' },
              { label: `#${c.caseNumber}` },
            ]}
          />
        }
        actions={
          canManage && targets.length > 0 ? (
            <Button onClick={() => setTransitionOpen(true)}>Change status</Button>
          ) : undefined
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Badge tone={c.severity === 's1' ? 'error' : c.severity === 's2' ? 'warning' : 'info'}>
          {c.severity.toUpperCase()}
        </Badge>
        <StatusPill tone={CASE_STATUS_TONES[c.status]} label={titleCase(c.status)} />
        <SlaCell c={c} />
        <span className="text-xs text-text-muted">
          Assigned: {c.assignedToDisplayName ?? 'unassigned'} · acknowledge by{' '}
          {formatDateTime(c.sla.acknowledgeBy)} · resolve by {formatDateTime(c.sla.resolveBy)}
        </span>
      </div>

      <Card>
        <CardHeader title="Description" />
        <CardBody>
          <p className="whitespace-pre-wrap text-sm text-text-primary">{c.description}</p>
        </CardBody>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader
            title="Event log"
            subtitle="Append-only — every transition and communication"
          />
          <CardBody>
            {canManage && (
              <div className="mb-3 flex items-end gap-2">
                <div className="flex-1">
                  <Textarea
                    label="Internal comment"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                  />
                </div>
                <Button
                  size="sm"
                  loading={mutations.comment.isPending}
                  disabled={comment.trim().length < 3}
                  onClick={() =>
                    void mutations.comment
                      .mutateAsync({ body: comment })
                      .then(() => setComment(''))
                      .catch((err) =>
                        toast.error(err instanceof ApiError ? err.message : 'Rejected'),
                      )
                  }
                >
                  Add
                </Button>
              </div>
            )}
            <ol className="space-y-3" data-testid="case-events">
              {events.map((e) => (
                <li key={e.id} className="text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <Badge
                      tone={
                        e.kind === 'status_change'
                          ? 'info'
                          : e.kind === 'communication'
                            ? 'accent'
                            : 'neutral'
                      }
                    >
                      {e.kind.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-text-muted">
                      {e.actorDisplayName ?? 'System'} · {formatDateTime(e.occurredAt)}
                    </span>
                  </p>
                  {e.body && (
                    <p className="mt-0.5 whitespace-pre-wrap text-text-secondary">{e.body}</p>
                  )}
                  {e.kind === 'status_change' && e.meta && (
                    <p className="text-xs text-text-muted">
                      {String((e.meta as { from?: string }).from)} →{' '}
                      {String((e.meta as { to?: string }).to)}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Attachments"
            subtitle="png · jpeg · pdf · txt · csv, max 5MB, scanned; executables and clinical content rejected"
            actions={
              canManage ? (
                <>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".png,.jpg,.jpeg,.pdf,.txt,.csv"
                    className="sr-only"
                    aria-label="Choose attachment"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) upload(file);
                      e.target.value = '';
                    }}
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    loading={mutations.attach.isPending}
                    onClick={() => fileRef.current?.click()}
                  >
                    Upload
                  </Button>
                </>
              ) : undefined
            }
          />
          <CardBody>
            {attachments.length === 0 ? (
              <p className="text-sm text-text-muted">No attachments.</p>
            ) : (
              <ul className="divide-y divide-border">
                {attachments.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 py-2 text-sm">
                    <Link
                      to={`/api/v1/cases/${c.id}/attachments/${a.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="truncate text-link hover:underline"
                    >
                      {a.filename}
                    </Link>
                    <span className="shrink-0 text-xs text-text-muted tabular-nums">
                      {(a.sizeBytes / 1024).toFixed(0)} KB · {a.uploadedByDisplayName ?? '—'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={transitionOpen}
        onClose={() => setTransitionOpen(false)}
        title="Change case status"
        footer={
          <Button
            loading={mutations.transition.isPending}
            disabled={!toStatus || note.trim().length < 3}
            onClick={() =>
              void mutations.transition
                .mutateAsync({ toStatus, note, expectedVersion: c.version })
                .then(() => {
                  toast.success('Status updated');
                  setTransitionOpen(false);
                  setNote('');
                  setToStatus('');
                })
                .catch((err) =>
                  toast.error(
                    err instanceof ApiError && err.code === 'VERSION_CONFLICT'
                      ? 'Someone else changed this case first — reload and retry.'
                      : err instanceof ApiError
                        ? err.message
                        : 'Transition failed',
                  ),
                )
            }
          >
            Apply
          </Button>
        }
      >
        <div className="space-y-3">
          <Select
            label="New status"
            required
            value={toStatus}
            onChange={(e) => setToStatus(e.target.value)}
          >
            <option value="">Choose…</option>
            {targets.map((t) => (
              <option key={t} value={t}>
                {titleCase(t)}
              </option>
            ))}
          </Select>
          <Textarea label="Note" required value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      </Modal>
    </div>
  );
}
